import { round2 } from '@/utils/workTimeUtils';
import { AHV_IV_EO_RATE } from '@/config/constants';

export interface EmployeePayrollData {
  id: string;
  name: string;
  hourly_rate?: number;
  monthly_salary?: number;
  salary_type: string;
  birth_date?: string;
  tax_canton?: string;
  work_type: string;
  working_hours_per_week?: number;
}

export interface PayrollCalculationInput {
  employee: EmployeePayrollData;
  hoursWorked: number;
  vacationDaysTaken: number;
  periodStart: Date;
  periodEnd: Date;
}

export interface PayrollDeduction {
  type: string;
  description: string;
  rate_percent: number;
  base_amount: number;
  deduction_amount: number;
}

export interface PayrollCalculationResult {
  employee_id: string;
  gross_salary: number;
  net_salary: number;
  total_deductions: number;
  hours_worked: number;
  base_salary: number;
  deductions: PayrollDeduction[];
  vacation_days_taken: number;
  vacation_pay_included: boolean;
}

// Swiss tax rates by canton (simplified - in reality this would be much more complex)
const CANTON_TAX_RATES: Record<string, number> = {
  'ZH': 0.08, // Zurich
  'GL': 0.06, // Glarus
  'BE': 0.09, // Bern
  'AG': 0.07, // Aargau
  'SG': 0.075, // St. Gallen
  // Add more cantons as needed
};

// Swiss insurance rates (AHV_IV_EO sourced from config/constants.ts SSOT)
export const SWISS_INSURANCE_RATES = {
  AHV_IV_EO: AHV_IV_EO_RATE,
  ALV: 0.011, // 1.1% (up to CHF 148,200)
  ALV_SOLIDARITY: 0.005, // 0.5% (above CHF 148,200)
  ALV_THRESHOLD: 148200,
  BVG_MIN_AGE: 25,
  BVG_MAX_AGE: 65,
  BVG_RATES: {
    '25-34': 0.07, // 7%
    '35-44': 0.10, // 10%
    '45-54': 0.15, // 15%
    '55-65': 0.18, // 18%
  }
};

export function calculateAge(birthDate: string): number {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
}

export function getBVGRate(birthDate?: string): number {
  if (!birthDate) return 0;
  
  const age = calculateAge(birthDate);
  
  if (age < SWISS_INSURANCE_RATES.BVG_MIN_AGE || age > SWISS_INSURANCE_RATES.BVG_MAX_AGE) {
    return 0;
  }
  
  if (age <= 34) return SWISS_INSURANCE_RATES.BVG_RATES['25-34'];
  if (age <= 44) return SWISS_INSURANCE_RATES.BVG_RATES['35-44'];
  if (age <= 54) return SWISS_INSURANCE_RATES.BVG_RATES['45-54'];
  return SWISS_INSURANCE_RATES.BVG_RATES['55-65'];
}

export function calculateGrossSalary(input: PayrollCalculationInput): number {
  const { employee, hoursWorked } = input;
  
  if (employee.salary_type === 'monthly') {
    return employee.monthly_salary || 0;
  } else {
    return (employee.hourly_rate || 0) * hoursWorked;
  }
}

export function calculateTaxDeduction(grossSalary: number, canton?: string): PayrollDeduction {
  const rate = canton ? (CANTON_TAX_RATES[canton] || 0.08) : 0.08;
  const deductionAmount = grossSalary * rate;
  
  return {
    type: 'tax',
    description: `Cantonal Tax (${canton || 'Standard'})`,
    rate_percent: rate * 100,
    base_amount: grossSalary,
    deduction_amount: round2(deductionAmount),
  };
}

export function calculateAHVDeduction(grossSalary: number): PayrollDeduction {
  const deductionAmount = grossSalary * SWISS_INSURANCE_RATES.AHV_IV_EO;
  
  return {
    type: 'ahv',
    description: 'AHV/IV/EO',
    rate_percent: SWISS_INSURANCE_RATES.AHV_IV_EO * 100,
    base_amount: grossSalary,
    deduction_amount: round2(deductionAmount),
  };
}

export function calculateALVDeduction(grossSalary: number, annualSalary: number): PayrollDeduction {
  let rate = SWISS_INSURANCE_RATES.ALV;
  let description = 'ALV (Unemployment Insurance)';
  
  // Apply solidarity contribution for high earners
  if (annualSalary > SWISS_INSURANCE_RATES.ALV_THRESHOLD) {
    rate += SWISS_INSURANCE_RATES.ALV_SOLIDARITY;
    description = 'ALV + Solidarity Contribution';
  }
  
  const deductionAmount = grossSalary * rate;
  
  return {
    type: 'alv',
    description,
    rate_percent: rate * 100,
    base_amount: grossSalary,
    deduction_amount: round2(deductionAmount),
  };
}

export function calculateBVGDeduction(grossSalary: number, birthDate?: string): PayrollDeduction {
  const rate = getBVGRate(birthDate);
  const deductionAmount = grossSalary * rate;
  
  return {
    type: 'bvg',
    description: 'BVG (Pension Fund)',
    rate_percent: rate * 100,
    base_amount: grossSalary,
    deduction_amount: round2(deductionAmount),
  };
}

export function calculateAllDeductions(
  grossSalary: number, 
  employee: EmployeePayrollData
): PayrollDeduction[] {
  const deductions: PayrollDeduction[] = [];
  
  // Calculate annual salary for ALV threshold check
  const annualSalary = employee.salary_type === 'monthly' 
    ? grossSalary * 12 
    : grossSalary * 12; // Approximate for hourly workers
  
  // AHV/IV/EO (mandatory for all)
  deductions.push(calculateAHVDeduction(grossSalary));
  
  // ALV (unemployment insurance)
  deductions.push(calculateALVDeduction(grossSalary, annualSalary));
  
  // BVG (pension fund) - age dependent
  const bvgDeduction = calculateBVGDeduction(grossSalary, employee.birth_date);
  if (bvgDeduction.deduction_amount > 0) {
    deductions.push(bvgDeduction);
  }
  
  // Tax deduction
  deductions.push(calculateTaxDeduction(grossSalary, employee.tax_canton));
  
  return deductions;
}

export function calculateNetSalary(grossSalary: number, deductions: PayrollDeduction[]): number {
  const totalDeductions = deductions.reduce((sum, deduction) => sum + deduction.deduction_amount, 0);
  return round2(grossSalary - totalDeductions);
}

export function calculatePayroll(input: PayrollCalculationInput): PayrollCalculationResult {
  const grossSalary = calculateGrossSalary(input);
  const deductions = calculateAllDeductions(grossSalary, input.employee);
  const netSalary = calculateNetSalary(grossSalary, deductions);
  const totalDeductions = deductions.reduce((sum, d) => sum + d.deduction_amount, 0);
  
  return {
    employee_id: input.employee.id,
    gross_salary: grossSalary,
    net_salary: netSalary,
    total_deductions: totalDeductions,
    hours_worked: input.hoursWorked,
    base_salary: grossSalary,
    deductions,
    vacation_days_taken: input.vacationDaysTaken,
    vacation_pay_included: false, // TODO: Implement vacation pay logic
  };
}

export function validatePayrollInput(input: PayrollCalculationInput): string[] {
  const errors: string[] = [];
  
  if (!input.employee.id) {
    errors.push('Employee ID is required');
  }
  
  if (input.employee.salary_type === 'hourly' && !input.employee.hourly_rate) {
    errors.push('Hourly rate is required for hourly employees');
  }
  
  if (input.employee.salary_type === 'monthly' && !input.employee.monthly_salary) {
    errors.push('Monthly salary is required for salaried employees');
  }
  
  if (input.hoursWorked < 0) {
    errors.push('Hours worked cannot be negative');
  }
  
  if (input.vacationDaysTaken < 0) {
    errors.push('Vacation days taken cannot be negative');
  }
  
  return errors;
}